import React from 'react';
import { motion } from 'framer-motion';
import {
  Loader, CheckCircle, AlertCircle, FileText, Sparkles, Globe, TrendingUp, MapPin, Target
} from 'lucide-react';
import { FADE_IN } from '../../lib/anim';

const LINE_ENTER = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const ICON_MAP = {
  scan: <FileText size={18} />,
  edu: <Sparkles size={18} />,
  globe: <Globe size={18} />,
  path: <TrendingUp size={18} />,
  map: <MapPin size={18} />,
  spark: <Target size={18} />,
  done: <CheckCircle size={18} />,
  error: <AlertCircle size={18} />,
};

const ParseLine = ({ line, isLatest }) => {
  const isObj = typeof line === 'object';
  const text = isObj ? line.text : line;
  const icon = isObj ? line.icon : 'scan';
  const isDone = icon === 'done';
  const isError = icon === 'error';
  const latest = isLatest && !isDone;

  return (
    <motion.div
      {...LINE_ENTER}
      style={{
        display: 'flex', alignItems: 'center', gap: '16px',
        padding: '16px 20px',
        borderLeft: `2px solid ${isDone ? 'var(--success)' : isError ? 'var(--error)' : latest ? 'var(--brand-purple)' : 'var(--border-default)'}`,
        background: isDone ? 'var(--success-bg)' : latest ? 'var(--surface-active)' : 'transparent',
        transition: 'all 0.3s',
      }}
    >
      <div style={{
        width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isDone ? 'rgba(16,185,129,0.15)' : isError ? 'rgba(244,63,94,0.15)' : 'var(--bg-elevated)',
        color: isDone ? 'var(--success)' : isError ? 'var(--error)' : latest ? 'var(--brand-purple)' : 'var(--text-muted)',
        border: `1px solid ${isDone ? 'rgba(16,185,129,0.3)' : latest ? 'rgba(246,116,37,0.3)' : 'var(--border-default)'}`,
      }}>
        {latest && !isDone ? <Loader className="animate-spin" size={18} /> : (ICON_MAP[icon] || <FileText size={18} />)}
      </div>
      <span style={{
        fontSize: isDone ? '1rem' : '0.9375rem',
        fontWeight: isDone ? 600 : latest ? 500 : 400,
        color: isDone ? 'var(--success)' : isError ? 'var(--error)' : latest ? 'var(--text-primary)' : 'var(--text-muted)',
        fontFamily: isDone ? 'var(--font-heading)' : 'var(--font-body)',
      }}>
        {text}
      </span>
    </motion.div>
  );
};

export const ParsingStep = ({ parseLines }) => {
  const finished = parseLines.some(l => {
    const icon = typeof l === 'object' ? l.icon : '';
    return icon === 'done' || icon === 'error';
  });

  return (
    <motion.div {...FADE_IN}>
      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>We&apos;re building your profile</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>Hang tight — this takes a few seconds.</p>
      </div>

      <div data-testid="parsing-terminal" style={{ maxWidth: '520px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '0' }}>
        {parseLines.map((line, i) => (
          <ParseLine key={typeof line === 'object' ? line.text : line} line={line} isLatest={i === parseLines.length - 1} />
        ))}

        {parseLines.length > 0 && !finished && (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div style={{
              display: 'inline-block', width: '48px', height: '48px', borderRadius: '50%',
              border: '2px solid var(--border-default)', borderTopColor: 'var(--brand-purple)',
              animation: 'spin 1s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
      </div>
    </motion.div>
  );
};
